import { useId } from 'react';

import { cn, focusRing } from '@/design-system';

import { clientMessagesCopy as copy } from '../copy';

export interface VariableChipsProps {
    /** De namen zoals ze in de tekst komen: `client_name`. */
    variables: readonly string[];
    /** De namen zoals de eigenaar ze leest: "Klantnaam". */
    labels: Readonly<Record<string, string>>;
    onInsert: (name: string) => void;
    disabled?: boolean;
    /** Uitleg onder de knoppen, één alinea per variabele die meer is dan een waarde. */
    notes?: readonly string[];
}

/**
 * De variabelen van één bericht als knoppen: een klik zet `{{ client_name }}` in
 * de tekst waar de cursor stond.
 *
 * De knop pakt bij een muisklik de focus niet over (`preventDefault` op
 * mousedown), zodat de cursor in het veld blijft staan en je door kunt typen.
 * Met het toetsenbord bereik je de knoppen gewoon; het veld onthoudt dan waar
 * de cursor stond.
 */
export function VariableChips({
    variables,
    labels,
    onInsert,
    disabled = false,
    notes = [],
}: VariableChipsProps) {
    const titleId = useId();

    return (
        <div role="group" aria-labelledby={titleId}>
            <span
                id={titleId}
                className="block text-[12px] leading-[1.3] text-[var(--text-secondary)]"
            >
                {copy.variables}
            </span>
            <div className="mt-[7px] flex flex-wrap gap-[6px]">
                {variables.map((name) => {
                    const label = labels[name] ?? name;

                    return (
                        <button
                            key={name}
                            type="button"
                            disabled={disabled}
                            aria-label={copy.insert(label)}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => onInsert(name)}
                            className={cn(
                                'inline-flex min-h-[36px] cursor-pointer items-center gap-[6px] rounded-[20px] px-3',
                                'border border-[var(--accent-border)] bg-[var(--accent-tint)]',
                                'font-[family-name:var(--font-body)] text-[13px] leading-none text-[var(--accent-dark)]',
                                'hover:bg-[var(--accent-tint-hover)] disabled:cursor-not-allowed disabled:opacity-50',
                                focusRing,
                            )}
                        >
                            <span aria-hidden="true" className="text-[15px] leading-none">
                                +
                            </span>
                            {label}
                        </button>
                    );
                })}
            </div>
            <p className="m-0 mt-[6px] text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]">
                {copy.variablesHint}
            </p>
            {notes.map((note) => (
                <p
                    key={note}
                    className="m-0 mt-[4px] text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]"
                >
                    {note}
                </p>
            ))}
        </div>
    );
}
